import { Slot } from '@radix-ui/react-slot';
import { XIcon } from 'lucide-react';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { isDesktop } from 'react-device-detect';
import {
    Command,
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '../../../components/ui/command.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover.tsx';
import { SelectTriggerButton } from '../../../components/ui/select-trigger-button.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { cn } from '../../../lib/utils.ts';

type Option<TValue = string> = {
    value: TValue;
    label: string;
    subtitle?: string;
    disabled?: boolean;
};

type CreatableMultiSelectProps<TValue = string> = {
    options?: Option<TValue>[];
    value: TValue[];
    onChange: (value: TValue[]) => void;
    placeholder?: string;
    disabled?: boolean;
    variant?: 'default' | 'inline';
    className?: string;
    isValidNewOption: (inputValue: string) => boolean;
    onCreateOption: (inputValue: string) => TValue;
    onInvalidOption: (inputValue: string) => void;
    formatOptionLabel?: (option: Option<TValue>) => React.ReactNode;
    formatValueLabel?: (value: TValue, option: Option<TValue> | undefined) => React.ReactNode;
    errorMessage?: string;
};

function renderDefaultOptionLabel<TValue>(option: Option<TValue>): React.ReactNode {
    return (
        <div className="flex flex-col">
            <Text size="sm" color="neutral-950">
                {option.label}
            </Text>
            {option.subtitle && (
                <Text size="xs" color="neutral-500">
                    {option.subtitle}
                </Text>
            )}
        </div>
    );
}

function renderDefaultValueLabel<TValue>(val: TValue, option: Option<TValue> | undefined): React.ReactNode {
    if (option?.subtitle) {
        return `${option.label} (${option.subtitle})`;
    }
    return option?.label ?? String(val);
}

export function CreatableMultiSelect<TValue = string>({
    options = [],
    value,
    onChange,
    placeholder,
    disabled,
    variant = 'default',
    className,
    isValidNewOption,
    onCreateOption,
    onInvalidOption,
    formatOptionLabel,
    formatValueLabel,
    errorMessage,
}: CreatableMultiSelectProps<TValue>): React.JSX.Element {
    const [inputValue, setInputValue] = useState('');
    const [open, setOpen] = useState(false);
    const listRef = useRef<HTMLDivElement>(null);
    const { t } = useLocale();

    const filteredOptions = useMemo(
        () => options.filter((option) => !value.includes(option.value)),
        [options, value],
    );

    const optionsByValue = useMemo(() => {
        const map = new Map<TValue, Option<TValue>>();
        for (const option of options) {
            map.set(option.value, option);
        }
        return map;
    }, [options]);

    const handleOpenChange = useCallback((next: boolean): void => {
        setOpen(next);
        if (!next) {
            setInputValue('');
        }
    }, []);

    const addOption = useCallback(
        (val: TValue): void => {
            if (!value.includes(val)) {
                onChange([...value, val]);
            }
            setInputValue('');
            setOpen(false);
        },
        [value, onChange],
    );

    const removeValue = useCallback(
        (val: TValue): void => {
            onChange(value.filter((v) => v !== val));
        },
        [value, onChange],
    );

    const trimmed = inputValue.trim();
    const canCreate = !!trimmed && isValidNewOption(trimmed);

    const handleCreate = useCallback((): void => {
        if (!canCreate) {
            return;
        }
        addOption(onCreateOption(trimmed));
    }, [canCreate, addOption, onCreateOption, trimmed]);

    const handleCreateOnEnter = useCallback(
        (e: React.KeyboardEvent): void => {
            if (e.key !== 'Enter' || !trimmed) {
                return;
            }

            // If cmdk has a visible item to select, let it handle Enter natively.
            const hasVisibleItems = listRef.current?.querySelector('[cmdk-item]:not([aria-hidden="true"])');
            if (hasVisibleItems) {
                return;
            }

            e.preventDefault();
            if (!isValidNewOption(trimmed)) {
                onInvalidOption(trimmed);
                return;
            }
            addOption(onCreateOption(trimmed));
        },
        [trimmed, isValidNewOption, onInvalidOption, addOption, onCreateOption],
    );

    const renderOptionLabel = formatOptionLabel ?? renderDefaultOptionLabel;
    const renderValueLabel = formatValueLabel ?? renderDefaultValueLabel;

    const commandBody = (
        <>
            <CommandInput
                placeholder={placeholder}
                value={inputValue}
                onValueChange={setInputValue}
                onKeyDown={handleCreateOnEnter}
            />
            <CommandList ref={listRef}>
                <CommandEmpty>
                    {canCreate ? (
                        <div
                            role="option"
                            aria-selected="false"
                            tabIndex={0}
                            className="hover:bg-accent relative flex cursor-default select-none items-center rounded-lg px-2 text-sm"
                            onClick={handleCreate}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    handleCreate();
                                }
                            }}
                        >
                            {trimmed}
                        </div>
                    ) : (
                        t('nothing_found')
                    )}
                </CommandEmpty>
                <CommandGroup className={filteredOptions.length === 0 ? 'hidden' : ''}>
                    {filteredOptions.map((option) => (
                        <CommandItem
                            key={String(option.value)}
                            value={String(option.value)}
                            disabled={option.disabled}
                            keywords={option.subtitle ? [option.label, option.subtitle] : [option.label]}
                            onSelect={() => {
                                addOption(option.value);
                            }}
                        >
                            {renderOptionLabel(option)}
                        </CommandItem>
                    ))}
                </CommandGroup>
            </CommandList>
        </>
    );

    const triggerContent = (
        <SelectTriggerButton
            variant={variant}
            className={variant === 'inline' ? '' : 'h-auto min-h-8'}
            disabled={disabled}
            error={!!errorMessage}
        >
            <Text color="neutral-600" className="text-left">
                {placeholder}
            </Text>
        </SelectTriggerButton>
    );

    return (
        <div className={cn('flex w-full flex-col gap-2', className)}>
            {isDesktop ? (
                <Popover open={open} onOpenChange={handleOpenChange}>
                    <PopoverTrigger asChild>{triggerContent}</PopoverTrigger>
                    <PopoverContent
                        className="p-0"
                        side="bottom"
                        align="start"
                        style={{
                            minWidth: 'var(--radix-popover-trigger-width)',
                            width: 'var(--radix-popover-trigger-width)',
                        }}
                    >
                        <Command shouldFilter>{commandBody}</Command>
                    </PopoverContent>
                </Popover>
            ) : (
                <>
                    <Slot
                        onClick={() => {
                            if (!disabled) {
                                setOpen(true);
                            }
                        }}
                    >
                        {triggerContent}
                    </Slot>
                    <CommandDialog open={open} onOpenChange={handleOpenChange}>
                        {commandBody}
                    </CommandDialog>
                </>
            )}
            {!!errorMessage && (
                <Text size="xs" color="red-600">
                    {errorMessage}
                </Text>
            )}
            {value.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {value.map((val) => (
                        <span
                            key={String(val)}
                            className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs text-neutral-700"
                        >
                            {renderValueLabel(val, optionsByValue.get(val))}
                            {!disabled && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        removeValue(val);
                                    }}
                                    className="text-neutral-400 hover:text-neutral-600"
                                >
                                    <XIcon className="h-3 w-3" />
                                </button>
                            )}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

export type { Option as CreatableMultiSelectOption, CreatableMultiSelectProps };
